import * as v from 'valibot'
import { JOB_PRIORITIES, type JobPriority } from './jobs'

/**
 * Workflows — the studio's templates for a client's work.
 *
 * A workflow is a client-owned, ordered list of steps, and each step is a
 * kind of work (a job type) with the defaults a job born from it starts
 * with: its title, its description, its priority, its estimated hours.
 * Opening the work from a workflow creates one job per step, in order —
 * every field of every job still editable afterwards, because the template
 * proposes and the staff decide. The job remembers which workflow produced
 * it; the workflow's own later edits never reach back into that work.
 *
 * The template is a living draft: rename it, reshape its steps, reorder
 * them. Only work that has not happened is being changed, and a workflow no
 * job has used can simply be gone. One that has produced work is part of
 * those jobs' story and is refused.
 */

export interface WorkflowStep {
  id: string
  workflowId: string
  /** The kind of work the step opens. */
  typeId: string
  /** The type's name, joined for display — the console never stitches. */
  typeName: string
  /**
   * The job title the step proposes; null means the type's name stands in,
   * behind the workflow's own name, when the work is opened.
   */
  title: string | null
  description: string | null
  /** The priority the step proposes; null means the caller's shared choice. */
  priority: JobPriority | null
  /** The hours the step proposes for the work it opens. */
  estimatedHours: number | null
  orderIndex: number
  createdAt: string
}

export interface Workflow {
  id: string
  clientId: string
  /** The client's name, joined for display — the API owns the join. */
  clientName: string
  name: string
  description: string | null
  /** The steps in their order — the order the work opens in. */
  steps: WorkflowStep[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

const required = (max: number) => v.pipe(v.string(), v.trim(), v.minLength(1), v.maxLength(max))
/** Clearable text: a string to set it, null to clear it, absent to leave it. */
const nullableText = (max: number) => v.union([v.pipe(v.string(), v.trim(), v.maxLength(max)), v.null()])
/** A calendar date, and nothing more precise — schedule fields, not timestamps. */
const players = v.pipe(v.number(), v.integer(), v.minValue(0), v.maxValue(500))
const hours = v.pipe(v.number(), v.minValue(0), v.maxValue(10_000))
/** A three-letter currency code, uppercased on arrival — BDT, USD, whatever the work is counted in. */
const currencyCode = v.pipe(
  v.string(),
  v.trim(),
  v.transform((value) => value.toUpperCase()),
  v.regex(/^[A-Z]{3}$/, 'Use a three-letter currency code, like BDT or USD.'),
)

export const createWorkflowStepSchema = v.object({
  typeId: required(64),
  title: v.optional(nullableText(160)),
  description: v.optional(nullableText(2000)),
  priority: v.optional(v.union([v.picklist(JOB_PRIORITIES), v.null()])),
  estimatedHours: v.optional(v.union([hours, v.null()])),
})

export type CreateWorkflowStepInput = v.InferOutput<typeof createWorkflowStepSchema>

export const createWorkflowSchema = v.object({
  clientId: required(64),
  name: required(120),
  description: v.optional(nullableText(500)),
  steps: v.optional(v.pipe(v.array(createWorkflowStepSchema), v.maxLength(50))),
})

export type CreateWorkflowInput = v.InferOutput<typeof createWorkflowSchema>

/**
 * The mid-edit contract, as everywhere: any subset of the record at any
 * time, absent means unchanged, null means cleared, at least one change is
 * required. The client is not here — a template is built for the client it
 * was built for — and the steps are their own verbs.
 */
export const updateWorkflowSchema = v.pipe(
  v.object({
    name: v.optional(required(120)),
    description: v.optional(nullableText(500)),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateWorkflowInput = v.InferOutput<typeof updateWorkflowSchema>

export const updateWorkflowStepSchema = v.pipe(
  v.object({
    typeId: v.optional(required(64)),
    title: v.optional(nullableText(160)),
    description: v.optional(nullableText(2000)),
    priority: v.optional(v.union([v.picklist(JOB_PRIORITIES), v.null()])),
    estimatedHours: v.optional(v.union([hours, v.null()])),
  }),
  v.check((input) => Object.values(input).some((value) => value !== undefined), 'Nothing to change.'),
)

export type UpdateWorkflowStepInput = v.InferOutput<typeof updateWorkflowStepSchema>

/**
 * The whole list, in its new order — one decision, one audit line, and the
 * API renumbers in a single batch. A partial order is refused: the caller
 * reads the list and orders all of it.
 */
export const reorderWorkflowStepsSchema = v.pipe(
  v.object({ ids: v.pipe(v.array(required(64)), v.minLength(1)) }),
  v.check((input) => new Set(input.ids).size === input.ids.length, 'The order repeats a step.'),
)

export type ReorderWorkflowStepsInput = v.InferOutput<typeof reorderWorkflowStepsSchema>

/**
 * Opening a workflow's work: the fields every job born here shares, decided
 * once at the moment of opening. The steps fill the rest — each job's type,
 * title, description, priority and estimated hours come from its step, and
 * the money fields stay the studio's per-engagement decisions, untouched by
 * the template.
 */
export const instantiateWorkflowSchema = v.object({
  workflowId: required(64),
  publish: v.optional(v.boolean()),
  priority: v.optional(v.picklist(JOB_PRIORITIES)),
  playersNeeded: v.optional(v.union([players, v.null()])),
  currency: v.optional(currencyCode),
})

export type InstantiateWorkflowInput = v.InferOutput<typeof instantiateWorkflowSchema>
